import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocationSearchComponent } from './filters/location-search/location-search.component';
import { CategoriesSearchComponent } from './filters/categories-search/categories-search.component';
import { LanguageService } from '@core/i18n/language.service';
import { Subject, takeUntil, debounceTime } from 'rxjs';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, LocationSearchComponent, CategoriesSearchComponent],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() columns: any[] = [];
  @Input() rows: any[] = [];
  @Input() rowClickable = false;
  @Input() borderless = false;
  @Input() columnTemplates: Record<string, TemplateRef<any>> = {};
  @Input() isRowDisabled?: (row: any) => boolean;
  @Input() rowClassFn?: (row: any) => string | null | undefined;
  @Input() pageSize: number = 0;
  @Input() hidePaginationInfo = false;
  @Input() resetFilters: number = 0;
  /** Muestra un overlay ligero (spinner) sobre la tabla mientras se recargan datos. */
  @Input() loading = false;
  /** Mensaje personalizado para el estado vacío (null → usa la traducción genérica). */
  @Input() noDataMessage: string | null = null;
  /** Modo servidor: `rows` es ya la página actual (no se recorta localmente) y `totalPages` se calcula con `totalRecords`. */
  @Input() lazy = false;
  /** Total de filas en el backend, usado para paginar en modo `lazy`. */
  @Input() totalRecords = 0;
  /** Página actual controlada por el padre en modo `lazy` (1-based). */
  @Input() page = 1;
  @Output() rowSelected = new EventEmitter<any>();
  @Output() rowDoubleClicked = new EventEmitter<any>();
  @Output() rowHovered = new EventEmitter<any | null>();
  @Output() pageChange = new EventEmitter<{ page: number; pageSize: number }>();
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('bodyContainer') bodyContainer!: ElementRef<HTMLDivElement>;

  showFilters: Record<string, boolean> = {};
  private filterMenuAnchors: Record<string, HTMLElement> = {};
  searchValues: Record<string, string> = {};
  sortedColumns: Record<string, 'asc' | 'desc' | null> = {};
  selectedLocations: Record<string, boolean> = {};
  originalRows: any[] = [];
  gridTemplateColumns = '';
  initLocationSearch = '';
  checkSaves: any;
  currentPage = 1;
  totalPages = 1;
  pageSizeOptions = [5, 10, 25, 50, 100];
  displayedRows: any[] = [];
  Math = Math;
  width: number = 0;
  isCompactMode = false;
  initCategorySearch = '';
  categorySaves: Set<string> = new Set();
  selectedCategories: Record<string, boolean> = {};
  categoryFilters: Record<string, { saves: Set<string>; field: string }> = {}; // Para soportar múltiples filtros de categorías
  private resizeObserver?: ResizeObserver;
  private destroy$ = new Subject<void>();
  private lastResetKey = 0;
  private lazySearchChange$ = new Subject<string>();
  translateData: any;

  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLDivElement>;

  private lastTouchTime = 0;
  private lastTouchedRow: any = null;
  private readonly DOUBLE_TAP_DELAY = 300;

  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private readonly TAP_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 500;

  /* Component parameters:
    columns: array (required) - Array of column definitions
    rows: array (required) - Array of data rows
    rowClickable: boolean (optional) - If true, rows are clickable and emit rowSelected/rowDoubleClicked events
    borderless: boolean (optional) - If true, removes table border and rounds. If false, adds border-bottom to last row
    columnTemplates: object (optional) - Object mapping template names to TemplateRef instances
    isRowDisabled: function (optional) - (row) => boolean, disables click/double-click and dims a row
    rowClassFn: function (optional) - (row) => string, extra CSS class(es) applied to that row (e.g. status-based tinting)
    rowHovered: event (optional) - emits the hovered row on mouseenter, or null on mouseleave
    pageSize: number (optional) - Number of rows to display per page, if not set is disabled
    hidePaginationInfo: boolean (optional) - If true, hides the "Showing X to Y of Z rows" text
    lazy: boolean (optional) - Server-driven pagination mode. `rows` is treated as already being the
      current page (no local slicing); pagination math uses `totalRecords` and `page` instead.
      Page/page-size changes and per-column "search" filter changes are emitted via `pageChange`/
      `searchChange` instead of being applied locally, so the parent can re-fetch from the backend.
    totalRecords: number (required when lazy) - Total row count reported by the backend.
    page: number (optional when lazy) - Current 1-based page, controlled by the parent.
    pageChange: event (optional) - emits { page, pageSize } when lazy and the user changes page or page size.
    searchChange: event (optional) - emits the raw search text (debounced) when lazy and a "search" column changes.
    
    Column parameters:
    key: string (required) - The key to access the data in the row object
    label: string (required) - The header label for the column
    size: number (optional) - Fixed width (converted to rem)
    min: number (optional) - Minimum width (converted to rem)
    max: number (optional) - Maximum width (converted to rem)
    filters: array (optional) - Array of filter types to apply to the column
      - 'search': Enables a search filter for the column
      - 'sort'  : Enables ascending and descending sort options for the column
      - 'location-search': Enables location-based filtering
    template: string (optional) - To use this, define an ng-template in the component and set the column's template property to the template's name
    padding: string (optional) - CSS padding shorthand for this column's cells (e.g. '4px 2px'). Defaults to the table's normal padding if omitted.
    align: string (optional) - CSS text-align value for the column's header title (e.g. 'center'). Defaults to left.
  */

  /* Example
    this.columns = [
      { key: 'id', label: 'ID', filters: ['search'] },
      { key: 'nombre', label: 'Nombre', filters: ['search'] },
      { key: 'apellido', label: 'Apellido', filters: ['sort'] },
      { key: 'email', label: 'Correo', filters: ['search', 'sort'] }
      { key: 'status', label: 'Status', template: 'status' },
    ];

    this.rows = [
      { id: 1, nombre: 'Juan', apellido: 'Pérez', email: 'juan@test.com' },
      { id: 2, nombre: 'María', apellido: 'García', email: 'maria@test.com' },
      { id: 3, nombre: 'Carlos', apellido: 'López', email: 'carlos@test.com' }
    ];

    // To enable pagination:
    <app-table [columns]="columns" [rows]="rows" [pageSize]="value"></app-table>
  */

  constructor(
    public langService: LanguageService,
    private cdr: ChangeDetectorRef,
  ) {}

  private _onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' && event.key !== 'Enter') return;

    const openKey = Object.keys(this.showFilters).find((key) => this.showFilters[key]);
    if (!openKey) return;

    const target = event.target as HTMLElement;
    const menuEl = document.querySelector(`[data-filter-menu="${openKey}"]`);

    // Main text search has its own (keydown) handler (onFilterKeydown) with
    // stopPropagation. Skip here so it can run its own clear-and-close logic.
    const isMainSearch =
      target.classList.contains('simple-table__filter--search') &&
      !target.closest('app-categories-search') &&
      !target.closest('app-filter-location-search');
    if (isMainSearch) return;

    if (event.key === 'Enter') {
      // Enter only acts when focus is inside the menu (avoids closing on unrelated Enter).
      // Buttons fire a click event natively on Enter (sort buttons close via onSort).
      if (!menuEl?.contains(target)) return;
      if (target.tagName === 'BUTTON') return;
      this.closeFilter(openKey);
      return;
    }

    // Escape always closes the open filter, regardless of where focus currently is.
    // The focus can land on document.body when a previous filter's input is destroyed
    // (e.g. user clicks a different column header while a filter is open), and in that
    // case restricting to menuEl.contains(target) would silently swallow the key.
    //
    // Sub-component data is cleared only when the user was actively in that component.
    const focusInMenu = !!menuEl?.contains(target);
    const column = this.columns.find((c) => c.key === openKey);
    if (column && focusInMenu) {
      let needsApply = false;
      let needsSearchApply = false;

      // Only clear the sub-component whose element currently has focus, so that pressing
      // Escape from (e.g.) a sort button doesn't accidentally wipe the location filter.
      if (
        this.hasFilter(column, 'location-search') &&
        target.closest('app-filter-location-search')
      ) {
        this.clearLocationSearch(openKey);
        needsApply = true;
      }
      if (this.hasFilter(column, 'category-filter') && target.closest('app-categories-search')) {
        this.selectedCategories['map_categories'] = false;
        this.initCategorySearch = '';
        this.categorySaves = new Set();
        needsApply = true;
      }
      if (this.hasFilter(column, 'roles-filter') && target.closest('app-categories-search')) {
        this.selectedCategories['roles'] = false;
        delete this.categoryFilters['roles'];
        needsApply = true;
      }
      // Escape from the × clear-search button clears the active search value,
      // consistent with Escape from the search input itself.
      if (
        this.hasFilter(column, 'search') &&
        target.classList.contains('simple-table__clear-search')
      ) {
        this.searchValues[openKey] = '';
        needsApply = true;
        needsSearchApply = true;
      }

      if (needsApply && this.lazy && needsSearchApply) {
        this.lazySearchChange$.next('');
      } else if (needsApply) {
        this.applyFilters();
      }
    }

    this.closeFilter(openKey);
  };

  private _onDocumentClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    // Include sub-component selectors as defensive fallbacks in case the
    // teleported menu's ancestor chain is interrupted by an intermediate portal.
    const isFilterMenu = target.closest(
      '.simple-table__filter-menu, app-categories-search, app-filter-location-search',
    );
    const isFilterableHeader = target.closest('.simple-table__cell--header-filterable');

    if (!isFilterMenu && !isFilterableHeader) {
      Object.keys(this.showFilters).forEach((key) => {
        if (this.showFilters[key]) {
          this.returnMenuToAnchor(key);
        }
      });
      this.showFilters = {};
      // markForCheck is required because this listener fires outside Angular's event
      // system (document.addEventListener), so OnPush ancestor components won't be
      // marked dirty automatically — without this the @if never re-evaluates.
      this.cdr.markForCheck();
    }
  };

  private returnMenuToAnchor(key: string): void {
    const menu = document.querySelector(`[data-filter-menu="${key}"]`) as HTMLElement;
    if (menu && menu.parentElement === document.body) {
      const anchor = this.filterMenuAnchors[key];
      if (anchor?.isConnected) {
        anchor.appendChild(menu);
      } else {
        menu.remove();
      }
      delete this.filterMenuAnchors[key];
    }
  }

  ngOnInit() {
    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._onDocumentKeydown, true);
    this.langService
      .getSharedDictionary('table')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.translateData = data;
      });

    this.lazySearchChange$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe((value) => this.searchChange.emit(value));

    this.originalRows = [...this.rows];
    this.calculateGridTemplate();
    this.updatePagination();
    this.setupResizeObserver();

    // Inicializar los Sets
    this.categorySaves = new Set();
    this.checkSaves = {
      territories: new Set<string>(),
      division: new Set<string>(),
      municipality: new Set<string>(),
    };
  }

  get emptySet(): Set<string> {
    return new Set<string>();
  }

  ngOnChanges() {
    if (this.resetFilters !== this.lastResetKey) {
      this.lastResetKey = this.resetFilters;
      this.clearAllTableFilters();
    }

    this.originalRows = [...this.rows];
    this.calculateGridTemplate();
    const hasActiveFilters =
      Object.keys(this.searchValues).some((k) => this.searchValues[k]?.trim()) ||
      Object.values(this.selectedLocations).some((v) => v) ||
      Object.values(this.selectedCategories).some((v) => v);
    if (hasActiveFilters) {
      this.applyFilters();
    } else {
      this.updatePagination();
    }
  }

  ngOnDestroy() {
    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._onDocumentKeydown, true);
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.width = entry.contentRect.width;
        this.isCompactMode = this.width < 500;
      }
    });

    setTimeout(() => {
      if (this.tableWrapper?.nativeElement) {
        this.resizeObserver?.observe(this.tableWrapper.nativeElement);
        this.width = this.tableWrapper.nativeElement.offsetWidth;
        this.isCompactMode = this.width < 500;
      }
    }, 0);
  }

  private calculateGridTemplate(): void {
    if (!this.columns || this.columns.length === 0) return;

    const columnDefinitions = this.columns.map((column) => {
      if (column.size) {
        return `${column.size}rem`;
      } else if (column.min && column.max) {
        return `minmax(${column.min}rem, ${column.max}rem)`;
      } else if (column.min) {
        return `minmax(${column.min}rem, 1fr)`;
      } else if (column.max) {
        return `minmax(3rem, ${column.max}rem)`;
      } else {
        return '1fr';
      }
    });

    this.gridTemplateColumns = columnDefinitions.join(' ');
  }

  hasFilter(column: any, filterType: string): boolean {
    return column.filters && column.filters.includes(filterType);
  }

  hasActiveFilter(columnKey: string): boolean {
    const hasSearch = this.searchValues[columnKey] && this.searchValues[columnKey].trim() !== '';
    const hasSort =
      this.sortedColumns[columnKey] !== null && this.sortedColumns[columnKey] !== undefined;
    const hasLocationSearch =
      this.selectedLocations[columnKey] === true && this.initLocationSearch !== '';
    const hasCategory =
      this.selectedCategories[columnKey] === true && this.initCategorySearch !== '';

    return hasSearch || hasSort || hasLocationSearch || hasCategory;
  }

  getCellValue(row: any, column: any): any {
    const keys = column.key.split('.');
    let value = row;

    for (const key of keys) {
      if (value !== null && value !== undefined) {
        value = value[key];
      } else {
        return '-';
      }
    }

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return value;
  }

  toggleFilters(columnKey: string, event?: Event): void {
    Object.keys(this.showFilters).forEach((key) => {
      if (key !== columnKey && this.showFilters[key]) {
        this.returnMenuToAnchor(key);
        this.showFilters[key] = false;
      }
    });

    const wasOpen = this.showFilters[columnKey];
    if (wasOpen) {
      this.returnMenuToAnchor(columnKey);
    }
    this.showFilters[columnKey] = !wasOpen;

    if (this.showFilters[columnKey] && event) {
      const anchor = event.currentTarget as HTMLElement;
      setTimeout(() => {
        this.positionFilterMenu(columnKey, anchor);
      }, 0);
    }
  }

  private positionFilterMenu(columnKey: string, anchorCell: HTMLElement): void {
    let menu = document.querySelector(`[data-filter-menu="${columnKey}"]`) as HTMLElement;

    if (!anchorCell || !menu) return;

    const button =
      (anchorCell.querySelector('.simple-table__filter--button') as HTMLElement) ?? anchorCell;

    if (menu.parentElement?.classList.contains('simple-table__cell--header')) {
      this.filterMenuAnchors[columnKey] = anchorCell;
      document.body.appendChild(menu);
    }

    const buttonRect = button.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const menuHeight = menuRect.height;

    let top: number;

    if (spaceBelow >= menuHeight || spaceBelow > spaceAbove) {
      top = buttonRect.bottom + 4;
    } else {
      top = buttonRect.top - menuHeight - 4;
    }

    let left = buttonRect.right - menuRect.width;

    const columnIndex = this.columns.findIndex((col) => col.key === columnKey);
    if (columnIndex <= 1) {
      left = buttonRect.left;
    }

    if (left < 0) {
      left = 8;
    } else if (left + menuRect.width > viewportWidth) {
      left = viewportWidth - menuRect.width - 8;
    }

    if (top < 0) {
      top = 8;
    } else if (top + menuRect.height > viewportHeight) {
      top = viewportHeight - menuRect.height - 8;
    }

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;

    setTimeout(() => {
      const searchInput = menu.querySelector('.simple-table__filter--search') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 50);
  }

  onSearch(event: Event, columnKey: string): void {
    const target = event.target as HTMLInputElement;
    this.searchValues[columnKey] = target.value.toLowerCase();

    if (this.lazy) {
      this.lazySearchChange$.next(target.value);
      return;
    }

    this.applyFilters();
  }

  onFilterKeydown(event: KeyboardEvent, columnKey: string): void {
    if (event.key === 'Enter') {
      // stopPropagation prevents onFilterMenuKeydown from also firing on the
      // same event, since this handler has more specific logic for the main search.
      event.stopPropagation();
      this.closeFilter(columnKey);
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      this.clearSearch(columnKey);
      this.closeFilter(columnKey);
    }
  }

  private closeFilter(columnKey: string): void {
    this.returnMenuToAnchor(columnKey);
    this.showFilters[columnKey] = false;
    this.cdr.markForCheck();
  }

  private applySortToRows(rows: any[], field: string, direction: 'asc' | 'desc'): void {
    rows.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];
      const numA = Number(valueA);
      const numB = Number(valueB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return direction === 'asc' ? numA - numB : numB - numA;
      }
      const strA = String(valueA || '').toLowerCase();
      const strB = String(valueB || '').toLowerCase();
      return direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  }

  onSort(columnKey: string, direction: 'asc' | 'desc'): void {
    Object.keys(this.sortedColumns).forEach((key) => {
      if (key !== columnKey) {
        this.sortedColumns[key] = null;
      }
    });

    this.sortedColumns[columnKey] = direction;

    const col = this.columns.find((c: any) => c.key === columnKey);
    const sortField = (col as any)?.sortKey || columnKey;
    this.applySortToRows(this.rows, sortField, direction);

    this.updatePagination();
    this.closeFilter(columnKey);
  }

  clearSearch(columnKey: string): void {
    this.searchValues[columnKey] = '';

    if (this.lazy) {
      this.lazySearchChange$.next('');
      return;
    }

    this.applyFilters();
  }

  clearAllFilters(columnKey: string): void {
    const hadSearch = !!this.searchValues[columnKey];
    this.searchValues[columnKey] = '';
    this.sortedColumns[columnKey] = null;
    this.clearLocationSearch(columnKey);
    this.clearCategorySearch(columnKey);

    if (this.lazy && hadSearch) {
      this.lazySearchChange$.next('');
      return;
    }

    this.applyFilters();
  }
  clearCategorySearch(columnKey: string): void {
    this.selectedCategories[columnKey] = false;
    this.initCategorySearch = '';
    this.categorySaves = new Set();
  }
  clearLocationSearch(columnKey: string): void {
    this.selectedLocations[columnKey] = false;
    this.initLocationSearch = '';
    this.checkSaves = {
      territories: new Set<string>(),
      division: new Set<string>(),
      municipality: new Set<string>(),
    };
  }

  onRowClick(row: any, event?: MouseEvent): void {
    if (this.isRowDisabled && this.isRowDisabled(row)) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (this.rowClickable) {
      const target = event?.target as HTMLElement;
      if (
        target?.tagName === 'BUTTON' ||
        target?.tagName === 'A' ||
        target?.tagName === 'INPUT' ||
        target?.tagName === 'SELECT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.closest('button') ||
        target?.closest('a') ||
        target?.closest('input') ||
        target?.closest('select') ||
        target?.closest('textarea') ||
        target?.classList.contains('delete-btn') ||
        target?.closest('.delete-btn')
      ) {
        return;
      }

      if (event && 'ontouchstart' in window) {
        const timeSinceTouch = Date.now() - this.touchStartTime;
        if (timeSinceTouch < 100) {
          event.preventDefault();
          return;
        }
      }
      this.rowSelected.emit(row);
    }
  }

  onRowMouseEnter(row: any): void {
    this.rowHovered.emit(row);
  }

  onRowMouseLeave(): void {
    this.rowHovered.emit(null);
  }

  onRowDoubleClick(row: any): void {
    if (this.isRowDisabled && this.isRowDisabled(row)) {
      return;
    }

    if (this.rowClickable) {
      this.rowDoubleClicked.emit(row);
    }
  }

  onRowTouchStart(row: any, event: TouchEvent): void {
    if (!this.rowClickable) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
  }

  onRowTouchEnd(row: any, event: TouchEvent): void {
    if (!this.rowClickable) return;

    const target = event.target as HTMLElement;
    if (
      target?.tagName === 'BUTTON' ||
      target?.tagName === 'A' ||
      target?.tagName === 'INPUT' ||
      target?.tagName === 'SELECT' ||
      target?.tagName === 'TEXTAREA' ||
      target?.closest('button') ||
      target?.closest('a') ||
      target?.closest('input') ||
      target?.closest('select') ||
      target?.closest('textarea') ||
      target?.classList.contains('delete-btn') ||
      target?.closest('.delete-btn')
    ) {
      this.resetTouchTracking();
      return;
    }

    const touch = event.changedTouches[0];
    const touchEndX = touch.clientX;
    const touchEndY = touch.clientY;
    const touchEndTime = Date.now();

    const deltaX = Math.abs(touchEndX - this.touchStartX);
    const deltaY = Math.abs(touchEndY - this.touchStartY);
    const touchDuration = touchEndTime - this.touchStartTime;

    if (
      deltaX < this.TAP_THRESHOLD &&
      deltaY < this.TAP_THRESHOLD &&
      touchDuration < this.TAP_TIME_THRESHOLD
    ) {
      const currentTime = Date.now();
      const timeDiff = currentTime - this.lastTouchTime;

      if (timeDiff < this.DOUBLE_TAP_DELAY && this.lastTouchedRow === row) {
        event.preventDefault();
        this.rowDoubleClicked.emit(row);
        this.lastTouchTime = 0;
        this.lastTouchedRow = null;
      } else {
        this.lastTouchTime = currentTime;
        this.lastTouchedRow = row;

        setTimeout(() => {
          if (this.lastTouchedRow === row && this.lastTouchTime === currentTime) {
            this.rowSelected.emit(row);
          }
        }, this.DOUBLE_TAP_DELAY);
      }
    }

    this.resetTouchTracking();
  }

  onRowTouchCancel(row: any, event: TouchEvent): void {
    this.resetTouchTracking();
  }

  private resetTouchTracking(): void {
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
  }

  private clearAllTableFilters(): void {
    this.searchValues = {};
    this.sortedColumns = {};
    this.selectedLocations = {};
    this.selectedCategories = {};
    this.categoryFilters = {};
    this.categorySaves = new Set();
    this.checkSaves = {
      territories: new Set<string>(),
      division: new Set<string>(),
      municipality: new Set<string>(),
    };
    this.showFilters = {};
    this.initLocationSearch = '';
    this.initCategorySearch = '';
    this.currentPage = 1;
  }

  private normalizeForSearch(s: string): string {
    return String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  private applyFilters(): void {
    this.rows = [...this.originalRows];

    // 1. Aplicar búsqueda — busca en todos los valores primitivos del row
    Object.keys(this.searchValues).forEach((columnKey) => {
      const searchValue = this.searchValues[columnKey];
      if (searchValue && searchValue.trim() !== '') {
        this.rows = this.rows.filter((row) => {
          const cellValue = row[columnKey];
          if (
            cellValue === null ||
            cellValue === undefined ||
            Array.isArray(cellValue) ||
            typeof cellValue === 'object'
          ) {
            return false;
          }
          return this.normalizeForSearch(cellValue).includes(this.normalizeForSearch(searchValue));
        });
      }
    });

    // 2. Aplicar filtro de ubicación
    if (this.selectedLocations['map_address'] === true) {
      const selectedTerritories = Array.from(this.checkSaves.territories || []);
      const selectedDivisions = Array.from(this.checkSaves.division || []);
      const selectedMunicipalities = Array.from(this.checkSaves.municipality || []);

      this.rows = this.rows.filter((row) => {
        const address = row.address;

        if (selectedTerritories.length > 0) {
          const territory = address?.adr_addterr?.trim().toLowerCase();
          if (!selectedTerritories.includes(territory)) return false;
        }

        if (selectedDivisions.length > 0) {
          const division = address?.adr_addprv?.trim().toLowerCase().split('|')[0];
          if (!selectedDivisions.includes(division)) return false;
        }

        if (selectedMunicipalities.length > 0) {
          const municipality = address?.adr_addtwn?.trim().toLowerCase().split('|')[0];
          if (!selectedMunicipalities.includes(municipality)) return false;
        }

        return true;
      });
    }

    // 3. Aplicar filtro de categorías
    if (this.selectedCategories['map_categories'] === true && this.categorySaves.size > 0) {
      const selectedCategories = Array.from(this.categorySaves);

      this.rows = this.rows.filter((row) => {
        if (!row.map_categories || !Array.isArray(row.map_categories)) {
          return false;
        }

        return row.map_categories.some((cat: any) => {
          const catOrgSub = typeof cat === 'object' ? cat.ORG_sub : cat;
          return selectedCategories.includes(catOrgSub);
        });
      });
    }

    // Re-apply active sort after filtering
    const activeSortKey = Object.keys(this.sortedColumns).find(
      (k) => this.sortedColumns[k] !== null && this.sortedColumns[k] !== undefined,
    );
    if (activeSortKey) {
      const col = this.columns.find((c: any) => c.key === activeSortKey);
      const sortField = (col as any)?.sortKey || activeSortKey;
      this.applySortToRows(this.rows, sortField, this.sortedColumns[activeSortKey]!);
    }

    this.updatePagination();
  }

  private updatePagination(): void {
    if (!this.pageSize || this.pageSize <= 0) {
      this.displayedRows = this.rows;
      return;
    }

    if (this.lazy) {
      this.currentPage = this.page;
      this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
      this.displayedRows = this.rows;
      return;
    }

    this.totalPages = Math.ceil(this.rows.length / this.pageSize);

    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }

    if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    this.displayedRows = this.getPaginatedRows();
  }

  private getPaginatedRows(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.rows.slice(startIndex, endIndex);
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;

    this.currentPage = page;

    if (this.lazy) {
      this.pageChange.emit({ page, pageSize: this.pageSize });
      return;
    }

    this.updatePagination();
  }

  changePageSize(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value, 10);
    this.currentPage = 1;

    if (this.lazy) {
      this.pageChange.emit({ page: 1, pageSize: this.pageSize });
      return;
    }

    this.updatePagination();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = this.isCompactMode ? 4 : 5;

    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.isCompactMode) {
        // Compact mode: max 4 total elements
        if (this.currentPage <= 2) {
          // Start: [1] [2] ... [last]
          pages.push(1);
          pages.push(2);
          pages.push(-1);
          pages.push(this.totalPages);
        } else if (this.currentPage >= this.totalPages - 1) {
          // End: [1] ... [n-1] [n]
          pages.push(1);
          pages.push(-1);
          pages.push(this.totalPages - 1);
          pages.push(this.totalPages);
        } else {
          // Middle: [1] ... [current] ... [last]
          pages.push(1);
          pages.push(-1);
          pages.push(this.currentPage);
          pages.push(-1);
          pages.push(this.totalPages);
        }
      } else {
        // Normal mode: show up to 5 visible pages
        if (this.currentPage <= 3) {
          for (let i = 1; i <= Math.min(4, this.totalPages - 1); i++) {
            pages.push(i);
          }
          if (this.totalPages > 4) pages.push(-1);
          pages.push(this.totalPages);
        } else if (this.currentPage >= this.totalPages - 2) {
          pages.push(1);
          pages.push(-1);
          for (let i = Math.max(2, this.totalPages - 3); i <= this.totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push(-1);
          for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push(-1);
          pages.push(this.totalPages);
        }
      }
    }

    return pages;
  }
  onCategoryFilterChanged(filteredRows: any[]) {
    this.displayedRows = filteredRows;
  }

  hasTemplate(column: any): boolean {
    return column.template && this.columnTemplates[column.template];
  }

  getTemplate(column: any): TemplateRef<any> | null {
    return column.template ? this.columnTemplates[column.template] || null : null;
  }

  getTemplateContext(row: any, column: any): any {
    return {
      $implicit: this.getCellValue(row, column),
      row: row,
      column: column,
      value: this.getCellValue(row, column),
    };
  }

  onFilterLocationChanged($event: any) {
    const event = $event;
    if (event && Array.isArray(event.rows)) {
      this.selectedLocations = { ['map_address']: event.selectedLocation };
      this.checkSaves = event.selected;
    } else {
      this.selectedLocations = {};
      this.checkSaves = { territories: new Set(), division: new Set(), municipality: new Set() };
    }
    this.applyFilters();
  }

  onSearchValue($event: any) {
    const event = $event;
    if (event.length >= 2) {
      this.initLocationSearch = event;
    } else {
      this.initLocationSearch = '';
    }
  }
  onFilterCategoryChanged($event: any): void {
    if (!$event) return;

    const { selectedCategory, selected } = $event;

    this.selectedCategories['map_categories'] = selectedCategory;
    this.categorySaves = selected;

    this.applyFilters();
  }

  onFilterCategoryChangedForField(fieldName: string, $event: any): void {
    if (!$event) return;

    const { rows, selectedCategory, selected } = $event;

    this.selectedCategories[fieldName] = selectedCategory;
    this.categoryFilters[fieldName] = { saves: selected, field: fieldName };

    // Reset a originalRows y aplica el nuevo filtro
    this.rows = [...this.originalRows];

    if (selectedCategory && Array.isArray(rows)) {
      this.rows = rows;
    }

    this.updatePagination();
  }

  revealRow(match: (row: any) => boolean): boolean {
    if (this.lazy || !this.pageSize || this.pageSize <= 0) return false;

    const index = this.rows.findIndex(match);
    if (index < 0) return false;

    const targetPage = Math.floor(index / this.pageSize) + 1;
    if (targetPage !== this.currentPage) {
      this.currentPage = targetPage;
      this.updatePagination();
      // revealRow is called imperatively (e.g. from a map marker click), not from a template event
      this.cdr.markForCheck();
    }

    this.scrollRowIntoView(index % this.pageSize);
    return true;
  }

  private scrollRowIntoView(rowIndexInPage: number): void {
    setTimeout(() => {
      const rows =
        this.bodyContainer?.nativeElement?.querySelectorAll<HTMLElement>('.simple-table__row');
      rows?.[rowIndexInPage]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, 0);
  }
}
